// =============================================================================
// features/assistant/components/AssistantEdgeTab.tsx — Aba lateral do copiloto
// no celular (F51-S37; substitui o FAB do F30-S01).
//
// Por que existe: no celular o botão do copiloto vive na topbar, espremido entre
// os outros ícones, virando um alvo de toque pequeno para um recurso de uso
// diário. O F30-S01 resolveu com um círculo fixo no canto inferior direito,
// supondo o canto livre — não estava: o compositor do chat põe ali o Enviar e o
// fim do campo de texto, e a SaveBar das configurações comerciais os botões.
//
// Agora o atalho é uma aba estreita docada na borda DIREITA, no meio da tela,
// arrastável na vertical (posição lembrada neste aparelho), e some nas rotas
// com rodapé ancorado e enquanto o usuário digita (`edgeTab.ts`). O botão da
// topbar continua sempre disponível; no desktop a aba não existe (`md:hidden`).
//
// Não é uma segunda implementação do copiloto: é só mais um gatilho para o
// mesmo AssistantWorkspaceModal. Gating idêntico ao da topbar (flag +
// permissão): um atalho persistente para quem não pode usar o recurso seria
// pior do que não ter atalho nenhum.
// =============================================================================

import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../auth/useAuth';
import { TAB_HEIGHT_PX, TAB_HIT_WIDTH_PX, shouldHideAssistantTab } from '../edgeTab';
import { useEdgeTabPosition } from '../hooks/useEdgeTabPosition';
import { useEditableFocus } from '../hooks/useEditableFocus';

import { AssistantWorkspaceModal } from './AssistantWorkspaceModal';
import { SparkleIcon } from './SparkleIcon';

/**
 * Decide se o atalho mobile deve existir na árvore.
 *
 * Extraído do componente para ser testável sem renderizar React — convenção do
 * repositório (os testes de `features/` exercitam lógica pura).
 *
 * Regra: mesmo gate do botão da topbar. Diferente da topbar, porém, o atalho
 * fica permanentemente sobre o conteúdo — mostrá-lo a quem não pode usar o
 * copiloto seria pior do que não ter atalho. (Nome mantido do F30-S01.)
 */
export function shouldShowAssistantFab(
  flagEnabled: boolean,
  hasPermission: (permission: string) => boolean,
): boolean {
  return flagEnabled && hasPermission('ai_assistant:use');
}

/** A aba em si: só montada quando visível, então não captura foco nem toque. */
function EdgeTabButton({ onOpen }: { onOpen: () => void }): React.JSX.Element {
  const hintId = React.useId();
  const position = useEdgeTabPosition();
  const active = position.pressed || position.dragging;

  return (
    <>
      <button
        type="button"
        aria-label="Abrir assistente interno"
        aria-describedby={hintId}
        onClick={() => {
          if (position.consumeDragClick()) return;
          onOpen();
        }}
        onPointerDown={position.onPointerDown}
        onPointerMove={position.onPointerMove}
        onPointerUp={position.onPointerUp}
        onPointerCancel={position.onPointerCancel}
        onKeyDown={position.onKeyDown}
        // md:hidden — no desktop o botão da topbar já cumpre o papel.
        // touch-none SÓ aqui: o arrasto da aba não pode virar rolagem da página.
        // z-30: abaixo do overlay do menu lateral (z-40) e das barras fixas.
        className={[
          'group md:hidden fixed z-30 flex items-center justify-end',
          'touch-none select-none [-webkit-tap-highlight-color:transparent]',
          'focus:outline-none',
          dragOrRest(position.dragging),
          active
            ? 'opacity-100'
            : 'opacity-[0.82] [@media(hover:hover)]:hover:opacity-100 focus-visible:opacity-100',
          'motion-reduce:transition-none',
        ].join(' ')}
        style={{
          top: position.top,
          // Alvo de toque 44x56: maior que a parte visível (30 px), que fica
          // colada na borda. Sem o inset, a aba cai sob o recorte em paisagem.
          right: 'env(safe-area-inset-right, 0px)',
          width: TAB_HIT_WIDTH_PX,
          height: TAB_HEIGHT_PX,
        }}
      >
        <span
          aria-hidden="true"
          className={[
            'flex h-full w-[30px] items-center justify-center',
            'rounded-l-[var(--radius-md)] bg-azul shadow-e3',
            // Hover "Glow" (DS §8) — elemento da cor da marca.
            // Só em ponteiro com hover real: no toque o :hover "gruda" após o tap.
            '[@media(hover:hover)]:group-hover:shadow-glow-azul',
            'group-focus-visible:ring-2 group-focus-visible:ring-azul',
            'group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-[var(--bg)]',
            'transition-[box-shadow,transform] duration-fast ease-out',
            'motion-reduce:transition-none',
            active ? '-translate-x-0.5' : 'translate-x-0',
          ].join(' ')}
          style={{ color: 'var(--text-on-brand)' }}
        >
          <SparkleIcon className="h-[18px] w-[18px]" />
        </span>
      </button>
      <span id={hintId} className="sr-only">
        Arraste na vertical ou use as setas para cima e para baixo para mover.
      </span>
    </>
  );
}

/** Durante o arrasto o `top` segue o dedo sem atraso; fora dele, suaviza. */
function dragOrRest(dragging: boolean): string {
  return dragging
    ? 'transition-opacity duration-fast ease-out'
    : 'transition-[top,opacity] duration-fast ease-out';
}

/** Atalho do copiloto no celular: aba na borda direita, some onde atrapalha. */
export function AssistantEdgeTab(): React.JSX.Element | null {
  const [chatOpen, setChatOpen] = React.useState(false);

  const { hasPermission } = useAuth();
  const { enabled: flagEnabled } = useFeatureFlag('ai.internal_assistant.enabled');
  const { pathname } = useLocation();
  const { editableFocused, keyboardOpen } = useEditableFocus();

  if (!shouldShowAssistantFab(flagEnabled, hasPermission)) return null;

  const hidden = shouldHideAssistantTab({ pathname, editableFocused, keyboardOpen });

  return (
    <>
      {!hidden && <EdgeTabButton onOpen={() => setChatOpen(true)} />}

      {/* O modal NÃO depende da aba: focar o campo dele esconde a aba, e o
          workspace precisa continuar aberto. */}
      {chatOpen && (
        <AssistantWorkspaceModal onClose={() => setChatOpen(false)} hasPermission={hasPermission} />
      )}
    </>
  );
}
